import { randomUUID } from 'node:crypto';
import {
  LAB_ORDER_STATES,
  LAB_ORDER_TRANSITIONS,
  LAB_RECEIVED_STATES,
  allocatePayment,
  inventoryValue,
  invoiceBalance,
  invoiceTotals,
  money,
  packsToBaseUnits,
  stockLevel,
  weightedAverageUnitCost,
  type LineInput,
} from '@oxyflows/domain';
import type { SyncEngine } from './sync.js';
import type { Row, Store } from './store.js';
import type { LocalRequest, LocalResponse } from './offline-api.js';

/**
 * Money, stock and the laboratory, answered from the device (spec §15,
 * ADR-0009).
 *
 * The patient's invoices and payments are in the local store, so the desk
 * works exactly as online: an invoice is made from the completed work, a
 * payment is applied to the open invoices oldest-first (or to the one the
 * patient is paying for), the account shows what is owed. What differs is
 * only what the server decides later: the invoice's official number (the
 * device shows a temporary reference), and the final spread of a payment if
 * another desk changed the same account meanwhile. Nothing is refused here
 * that the server would accept; nothing is invented that the server would
 * not record.
 */
const nowIso = () => new Date().toISOString();
const iso = (v: unknown): string => (v instanceof Date ? v.toISOString() : typeof v === 'string' ? (Number.isFinite(Date.parse(v)) ? new Date(v).toISOString() : v) : String(v));
const num = (v: unknown): number => Number(v ?? 0) || 0;
const localDate = (ms: number) => new Date(ms).toISOString().slice(0, 10);

interface Me {
  user: { id: string; fullName: string; membershipId: string | null; permissions: string[] };
}

export class OfflineBusiness {
  constructor(private readonly engine: SyncEngine) {}

  private unavailable(req: LocalRequest, text: (k: 'offline' | 'notFound') => string): LocalResponse {
    return { status: 503, body: { code: 'offline_unavailable', message: text('offline') } };
  }

  private notFound(text: (k: 'offline' | 'notFound') => string): LocalResponse {
    return { status: 404, body: { code: 'not_found', message: text('notFound') } };
  }

  private clinic(store: Store): Row {
    return (store.rows('clinic')[0] ?? {}) as Row;
  }

  private patientRef(store: Store, id: string) {
    const p = store.getRow('patient', id);
    return p ? { id: p.id, patientNumber: p.patient_number, givenName: p.given_name, familyName: p.family_name, givenNameAlt: p.given_name_alt ?? null, familyNameAlt: p.family_name_alt ?? null } : null;
  }

  private userNames(store: Store): Map<string, string> {
    return new Map(store.rows('app_user').map((u) => [String(u.id), String(u.fullName ?? u.full_name ?? '')]));
  }

  // ================================================================ finance
  private lineInput(l: Row): LineInput {
    return { quantity: num(l.quantity) || 1, unitPriceMinor: num(l.unit_price_minor), discountMinor: num(l.discount_minor), taxMinor: num(l.tax_minor), currency: String(l.currency ?? '') };
  }

  /** Total, paid and outstanding of one invoice from the rows the device holds. */
  private balance(store: Store, inv: Row) {
    const lines = store.rows('invoice_line').filter((l) => l.invoice_id === inv.id);
    const allocated = store.rows('payment_allocation').filter((a) => a.invoice_id === inv.id).reduce((t, a) => t + num(a.amount_minor), 0);
    const credited = store.rows('credit_note').filter((c) => c.invoice_id === inv.id && c.status === 'posted').reduce((t, c) => t + num(c.amount_minor), 0);
    const b = invoiceBalance({ currency: String(inv.currency), lines: lines.map((l) => this.lineInput(l)), allocatedMinor: allocated, creditedMinor: credited });
    return { lines, totalMinor: b.total.minor, paidMinor: b.paid.minor, outstandingMinor: b.outstanding.minor, creditedMinor: credited, settled: b.settled, overpaid: b.overpaid };
  }

  /** A payment's reversal status from the rows the device holds. */
  private reversed(store: Store, paymentId: string): boolean {
    return store.rows('payment').some((p) => p.reverses_id === paymentId);
  }

  /** Incoming money of a patient not yet applied to an invoice, oldest first, net of refunds. */
  private credits(store: Store, patientId: string, currency: string): { paymentId: string; remainingMinor: number }[] {
    const payments = store
      .rows('payment')
      .filter((p) => p.patient_id === patientId && p.direction === 'incoming' && p.currency === currency && !p.reverses_id && !this.reversed(store, String(p.id)))
      .sort((a, b) => Date.parse(String(a.received_at)) - Date.parse(String(b.received_at)));
    const allocations = store.rows('payment_allocation');
    const credits = payments
      .map((p) => ({ paymentId: String(p.id), remainingMinor: num(p.amount_minor) - allocations.filter((a) => a.payment_id === p.id).reduce((t, a) => t + num(a.amount_minor), 0) }))
      .filter((c) => c.remainingMinor > 0);
    let refunded = store
      .rows('payment')
      .filter((p) => p.patient_id === patientId && p.direction === 'outgoing' && p.currency === currency && !p.reverses_id && !this.reversed(store, String(p.id)))
      .reduce((t, p) => t + num(p.amount_minor), 0);
    for (const c of credits) {
      if (refunded <= 0) break;
      const take = Math.min(refunded, c.remainingMinor);
      c.remainingMinor -= take;
      refunded -= take;
    }
    return credits.filter((c) => c.remainingMinor > 0);
  }

  private openInvoices(store: Store, patientId: string, currency: string) {
    return store
      .rows('invoice')
      .filter((i) => i.patient_id === patientId && i.status === 'posted' && i.currency === currency)
      .map((i) => ({ invoiceId: String(i.id), outstandingMinor: this.balance(store, i).outstandingMinor, issuedAt: i.issued_at ? new Date(String(i.issued_at)) : null }))
      .filter((i) => i.outstandingMinor > 0);
  }

  private invoiceView(store: Store, inv: Row) {
    const b = this.balance(store, inv);
    return {
      id: inv.id,
      number: inv.number ?? null,
      provisionalRef: inv.provisional_ref ?? null,
      status: inv.status,
      currency: inv.currency,
      issuedAt: inv.issued_at ?? null,
      dueAt: inv.due_at ?? null,
      createdAt: inv.created_at ?? null,
      patient: this.patientRef(store, String(inv.patient_id)),
      totalMinor: b.totalMinor,
      paidMinor: b.paidMinor,
      outstandingMinor: b.outstandingMinor,
      provisional: (inv as { __provisional?: boolean }).__provisional === true,
    };
  }

  private unbilledOf(store: Store, patientId?: string) {
    return store
      .rows('treatment')
      .filter((t) => (!patientId || t.patient_id === patientId) && t.status === 'completed' && t.billable !== false && (t.billing_source_key === null || t.billing_source_key === undefined))
      .sort((a, b) => Date.parse(String(b.performed_at ?? b.completed_at ?? b.created_at)) - Date.parse(String(a.performed_at ?? a.completed_at ?? a.created_at)));
  }

  private nextProvisionalRef(store: Store): string {
    const tag = this.engine.deviceId.replace(/-/g, '').slice(0, 4).toUpperCase();
    const used = store.rows('invoice').map((i) => String(i.provisional_ref ?? '')).filter((r) => r.startsWith(`TMP-${tag}-`)).map((r) => Number(r.split('-').pop()) || 0);
    return `TMP-${tag}-${String(Math.max(0, ...used) + 1).padStart(3, '0')}`;
  }

  finance(req: LocalRequest, store: Store, me: Me, seg: string[], text: (k: 'offline' | 'notFound') => string): LocalResponse {
    const sub = seg[2];
    const clinic = this.clinic(store);
    const clinicCurrency = String(clinic.default_currency ?? 'USD');

    // ---------------------------------------------------------- invoices
    if (sub === 'invoices') {
      const id = seg[3];
      if (req.method === 'GET' && !id) {
        const patientId = req.query.get('patientId');
        const limit = Math.min(Number(req.query.get('limit') ?? 200) || 200, 1000);
        const list = store
          .rows('invoice')
          .filter((i) => !patientId || i.patient_id === patientId)
          .sort((a, b) => Date.parse(String(b.created_at)) - Date.parse(String(a.created_at)));
        return { status: 200, body: { truncated: list.length > limit, limit, invoices: list.slice(0, limit).map((i) => this.invoiceView(store, i)), offline: true } };
      }
      if (req.method === 'POST' && !id) {
        const body = (req.body ?? {}) as Record<string, unknown>;
        const patientId = String(body.patientId ?? '');
        if (!store.getRow('patient', patientId)) return this.notFound(text);
        const lines = Array.isArray(body.lines) ? (body.lines as Record<string, unknown>[]) : [];
        if (lines.length === 0) return { status: 400, body: { code: 'validation_failed', message: 'An invoice needs at least one line.' } };
        const invoiceId = typeof body.id === 'string' ? body.id : randomUUID();
        const currency = typeof body.currency === 'string' ? body.currency : clinicCurrency;
        const provisionalRef = this.nextProvisionalRef(store);
        const dependsOn = new Set<string>();
        const rows: { entity: string; id: string; data: Row; patientId: string }[] = [];
        for (const line of lines) {
          const tid = typeof line.treatmentId === 'string' ? line.treatmentId : null;
          if (tid) {
            const t = store.getRow('treatment', tid);
            if (!t || t.patient_id !== patientId) return { status: 404, body: { code: 'treatment_not_found', message: "That treatment is not on this patient's record." } };
            if (t.billing_source_key) return { status: 409, body: { code: 'already_billed', message: 'That treatment has already been billed on another invoice.' } };
            for (const op of store.pendingFor(tid)) dependsOn.add(op);
          }
          const lineId = randomUUID();
          rows.push({ entity: 'invoice_line', id: lineId, patientId, data: { id: lineId, clinic_id: this.engine.active?.clinicId, invoice_id: invoiceId, treatment_id: tid, description: String(line.description ?? ''), quantity: num(line.quantity) || 1, unit_price_minor: num(line.unitPriceMinor), discount_minor: num(line.discountMinor), tax_minor: num(line.taxMinor), currency, source_key: tid ? `local:${tid}:invoice_line` : null, created_at: nowIso() } });
        }
        rows.unshift({ entity: 'invoice', id: invoiceId, patientId, data: { id: invoiceId, clinic_id: this.engine.active?.clinicId, patient_id: patientId, number: null, provisional_ref: provisionalRef, status: 'draft', currency, issued_at: null, due_at: body.dueAt ?? null, notes: body.notes ?? null, voided_at: null, void_reason: null, created_at: nowIso(), created_by: me.user.id, updated_at: nowIso(), version: 1 } });
        this.engine.record({ entity: 'invoice', entityId: invoiceId, action: 'create', payload: { ...body, id: undefined, provisionalRef, currency }, dependsOn: [...dependsOn], rows });
        // The work is now on an invoice; it leaves the unbilled list at once.
        for (const line of lines) {
          if (typeof line.treatmentId === 'string') store.patchRow('treatment', line.treatmentId, { billing_source_key: `local:${line.treatmentId}:invoice_line` });
        }
        return { status: 201, body: { id: invoiceId, provisionalRef, provisional: true } };
      }
      if (!id) return this.notFound(text);
      const inv = store.getRow('invoice', id);
      if (!inv) return this.notFound(text);
      if (req.method === 'GET' && seg.length === 4) {
        const b = this.balance(store, inv);
        const allocations = store.rows('payment_allocation').filter((a) => a.invoice_id === id);
        const payments = allocations.map((a) => {
          const p = store.getRow('payment', String(a.payment_id));
          return { id: a.id, amountMinor: num(a.amount_minor), method: p?.method ?? null, reference: p?.reference ?? null, at: p?.received_at ?? null };
        });
        const installments = store.rows('installment').filter((i) => i.invoice_id === id).sort((a, b) => num(a.sequence) - num(b.sequence));
        const totals = invoiceTotals(b.lines.map((l) => this.lineInput(l)), String(inv.currency));
        return {
          status: 200,
          body: {
            invoice: { id: inv.id, number: inv.number ?? null, provisionalRef: inv.provisional_ref ?? null, status: inv.status, currency: inv.currency, issuedAt: inv.issued_at ?? null, dueAt: inv.due_at ?? null, notes: inv.notes ?? null },
            lines: b.lines.map((l) => ({ id: l.id, description: l.description, quantity: num(l.quantity) || 1, unitPriceMinor: num(l.unit_price_minor), discountMinor: num(l.discount_minor), taxMinor: num(l.tax_minor), totalMinor: (num(l.quantity) || 1) * num(l.unit_price_minor) - num(l.discount_minor) + num(l.tax_minor), treatmentId: l.treatment_id ?? null })),
            payments,
            installments: installments.map((i) => ({ id: i.id, sequence: num(i.sequence), dueOn: i.due_on, amountMinor: num(i.amount_minor) })),
            totals: { subtotalMinor: totals.subtotal.minor, discountMinor: totals.discount.minor, taxMinor: totals.tax.minor, totalMinor: b.totalMinor, creditedMinor: b.creditedMinor, paidMinor: b.paidMinor, outstandingMinor: b.outstandingMinor, settled: b.settled, overpaid: b.overpaid },
            offline: true,
          },
        };
      }
      if (req.method === 'POST' && seg[4] === 'post') {
        if (inv.status === 'posted') return { status: 200, body: { id, number: inv.number ?? null, alreadyPosted: true } };
        if (inv.status === 'void') return { status: 400, body: { code: 'validation_failed', message: 'A voided invoice cannot be posted.' } };
        if (store.rows('invoice_line').filter((l) => l.invoice_id === id).length === 0) return { status: 400, body: { code: 'validation_failed', message: 'An invoice needs at least one line.' } };
        store.patchRow('invoice', id, { status: 'posted', issued_at: nowIso(), updated_at: nowIso() });
        this.engine.record({ entity: 'invoice', entityId: id, action: 'post', payload: {}, rows: [] });
        return { status: 200, body: { id, number: null, provisionalRef: inv.provisional_ref ?? null, alreadyPosted: false, provisional: true } };
      }
      if (req.method === 'DELETE' && seg.length === 4) {
        if (inv.status !== 'draft') return { status: 409, body: { code: 'invoice_not_draft', message: 'A posted invoice cannot be deleted. Issue a credit note against it instead.' } };
        const lines = store.rows('invoice_line').filter((l) => l.invoice_id === id);
        for (const l of lines) if (typeof l.treatment_id === 'string') store.patchRow('treatment', l.treatment_id, { billing_source_key: null });
        this.engine.record({ entity: 'invoice', entityId: id, action: 'delete', payload: {}, rows: [], deletes: [{ entity: 'invoice', id }, ...lines.map((l) => ({ entity: 'invoice_line', id: String(l.id) }))] });
        return { status: 200, body: { deleted: true } };
      }
      return this.unavailable(req, text);
    }

    // ---------------------------------------------------------- payments
    if (sub === 'payments') {
      const id = seg[3];
      if (req.method === 'GET' && !id) {
        const patientId = req.query.get('patientId');
        const direction = req.query.get('direction');
        const q = (req.query.get('q') ?? '').trim().toLowerCase();
        const includeReversed = req.query.get('includeReversed') !== 'false';
        const limit = Math.min(Number(req.query.get('limit') ?? 200) || 200, 1000);
        const list = store
          .rows('payment')
          .filter((p) => (!patientId || p.patient_id === patientId) && (!direction || p.direction === direction))
          .filter((p) => includeReversed || (!p.reverses_id && !this.reversed(store, String(p.id))))
          .sort((a, b) => Date.parse(String(b.received_at)) - Date.parse(String(a.received_at)));
        const views = list.map((p) => {
          const patient = p.patient_id ? store.getRow('patient', String(p.patient_id)) : null;
          const supplier = p.supplier_id ? store.getRow('supplier', String(p.supplier_id)) : null;
          const lab = p.laboratory_id ? store.getRow('laboratory', String(p.laboratory_id)) : null;
          return {
            id: p.id, direction: p.direction, amountMinor: num(p.amount_minor), currency: p.currency, method: p.method, reference: p.reference ?? null, category: p.category ?? null, receivedAt: p.received_at,
            isReversal: !!p.reverses_id, wasReversed: this.reversed(store, String(p.id)), reversalReason: p.reversal_reason ?? null,
            patient: patient ? { patientNumber: patient.patient_number, givenName: patient.given_name, familyName: patient.family_name } : null,
            counterparty: supplier ? { kind: 'supplier', name: supplier.name } : lab ? { kind: 'laboratory', name: lab.name } : patient ? { kind: 'patient', name: `${patient.given_name} ${patient.family_name}` } : null,
            provisional: (p as { __provisional?: boolean }).__provisional === true,
          };
        }).filter((v) => !q || [v.patient?.givenName, v.patient?.familyName, v.patient?.patientNumber, v.counterparty?.name, v.reference, v.category].some((s) => typeof s === 'string' && s.toLowerCase().includes(q)));
        return { status: 200, body: { truncated: views.length > limit, limit, payments: views.slice(0, limit), offline: true } };
      }
      if (req.method === 'POST' && !id) {
        const body = (req.body ?? {}) as Record<string, unknown>;
        const patientId = typeof body.patientId === 'string' ? body.patientId : null;
        const direction = body.direction === 'outgoing' ? 'outgoing' : 'incoming';
        // Money to a supplier or a laboratory, and refunds, need the server's
        // view of what is owed; a patient's payment does not.
        if (!patientId || direction !== 'incoming') return this.unavailable(req, text);
        if (!store.getRow('patient', patientId)) return this.notFound(text);
        const amount = Math.floor(num(body.amountMinor));
        if (!(amount > 0)) return { status: 400, body: { code: 'validation_failed', message: 'The amount must be positive.' } };
        const currency = typeof body.currency === 'string' ? body.currency : clinicCurrency;
        const paymentId = typeof body.id === 'string' ? body.id : randomUUID();
        const receivedAt = typeof body.receivedAt === 'string' ? iso(body.receivedAt) : nowIso();
        const open = this.openInvoices(store, patientId, currency);
        const named = typeof body.invoiceId === 'string' ? body.invoiceId : null;
        if (named && !open.some((i) => i.invoiceId === named)) return { status: 400, body: { code: 'invoice_not_open', message: 'That invoice is not open for this patient in this currency.' } };
        const plan = allocatePayment(money(amount, currency), open, named);
        const dependsOn = new Set<string>();
        if (named) for (const op of store.pendingFor(named)) dependsOn.add(op);
        const rows: { entity: string; id: string; data: Row; patientId: string }[] = [
          { entity: 'payment', id: paymentId, patientId, data: { id: paymentId, clinic_id: this.engine.active?.clinicId, direction, amount_minor: amount, currency, method: body.method ?? 'cash', reference: body.reference ?? null, patient_id: patientId, supplier_id: null, laboratory_id: null, purchase_invoice_id: null, lab_order_id: null, dentist_membership_id: null, employee_label: null, category: body.category ?? null, received_at: receivedAt, recorded_by: me.user.id, reverses_id: null, reversal_reason: null, source_key: null, created_at: nowIso() } },
        ];
        for (const a of plan.allocations) {
          for (const op of store.pendingFor(a.invoiceId)) dependsOn.add(op);
          const allocationId = randomUUID();
          rows.push({ entity: 'payment_allocation', id: allocationId, patientId, data: { id: allocationId, clinic_id: this.engine.active?.clinicId, payment_id: paymentId, invoice_id: a.invoiceId, amount_minor: a.amount.minor, currency, allocated_at: nowIso() } });
        }
        this.engine.record({ entity: 'payment', entityId: paymentId, action: 'create', payload: { ...body, id: undefined, idempotencyKey: undefined, patientId, direction, amountMinor: amount, currency, receivedAt }, dependsOn: [...dependsOn], rows });
        return { status: 201, body: { id: paymentId, duplicate: false, allocations: plan.allocations.map((a) => ({ invoiceId: a.invoiceId, amountMinor: a.amount.minor })), creditMinor: plan.unapplied.minor, provisional: true } };
      }
      return this.unavailable(req, text);
    }

    // ----------------------------------------------------------- account
    if (sub === 'patients' && seg[3] && seg[4] === 'account' && req.method === 'GET') {
      const patientId = seg[3];
      if (!store.getRow('patient', patientId)) return this.notFound(text);
      const invoices = store.rows('invoice').filter((i) => i.patient_id === patientId).sort((a, b) => Date.parse(String(b.created_at)) - Date.parse(String(a.created_at)));
      const currency = String(invoices.find((i) => i.status === 'posted')?.currency ?? clinicCurrency);
      const balances = invoices.map((i) => ({ i, b: this.balance(store, i) }));
      const totalOutstanding = balances.filter(({ i }) => i.status === 'posted').reduce((t, { b }) => t + b.outstandingMinor, 0);
      const credits = this.credits(store, patientId, currency);
      const creditMinor = credits.reduce((t, c) => t + c.remainingMinor, 0);
      const unbilled = this.unbilledOf(store, patientId);
      const payments = store.rows('payment').filter((p) => p.patient_id === patientId).sort((a, b) => Date.parse(String(b.received_at)) - Date.parse(String(a.received_at)));
      return {
        status: 200,
        body: {
          currency,
          outstandingMinor: totalOutstanding - creditMinor,
          creditMinor,
          unbilled: { count: unbilled.length, totalMinor: unbilled.reduce((t, u) => t + num(u.price_minor), 0), items: unbilled.map((u) => ({ id: u.id, nameEn: u.name_en, nameAr: u.name_ar, toothFdi: u.tooth_fdi ?? null, priceMinor: num(u.price_minor), currency: u.currency, performedAt: u.performed_at ?? u.completed_at ?? u.created_at })) },
          invoices: balances.map(({ i, b }) => ({ id: i.id, number: i.number ?? null, provisionalRef: i.provisional_ref ?? null, status: i.status, issuedAt: i.issued_at ?? null, dueAt: i.due_at ?? null, totalMinor: b.totalMinor, paidMinor: b.paidMinor, outstandingMinor: b.outstandingMinor, provisional: (i as { __provisional?: boolean }).__provisional === true })),
          payments: payments.map((p) => ({ id: p.id, direction: p.direction, amountMinor: num(p.amount_minor), currency: p.currency, method: p.method, reference: p.reference ?? null, at: p.received_at, isReversal: !!p.reverses_id, wasReversed: this.reversed(store, String(p.id)), category: p.category ?? null, provisional: (p as { __provisional?: boolean }).__provisional === true })),
          offline: true,
        },
      };
    }

    // ----------------------------------------------------------- unbilled
    if (sub === 'unbilled' && req.method === 'GET') {
      const rows = this.unbilledOf(store);
      const users = this.userNames(store);
      const byPatient = new Map<string, { patient: unknown; currency: string; totalMinor: number; items: unknown[] }>();
      for (const r of rows) {
        const pid = String(r.patient_id);
        const patient = store.getRow('patient', pid);
        if (!patient || patient.archived_at) continue;
        let entry = byPatient.get(pid);
        if (!entry) {
          entry = { patient: this.patientRef(store, pid), currency: String(r.currency), totalMinor: 0, items: [] };
          byPatient.set(pid, entry);
        }
        entry.totalMinor += num(r.price_minor);
        const m = r.dentist_membership_id ? store.getRow('membership', String(r.dentist_membership_id)) : null;
        entry.items.push({ id: r.id, nameEn: r.name_en, nameAr: r.name_ar, toothFdi: r.tooth_fdi ?? null, priceMinor: num(r.price_minor), performedAt: r.performed_at ?? r.completed_at ?? r.created_at, dentistName: m ? (users.get(String(m.user_id)) ?? null) : null });
      }
      const patients = [...byPatient.values()].sort((a, b) => String((a.patient as { familyName?: string })?.familyName ?? '').localeCompare(String((b.patient as { familyName?: string })?.familyName ?? '')));
      return { status: 200, body: { patients, totalMinor: patients.reduce((t, p) => t + p.totalMinor, 0), count: rows.length, offline: true } };
    }

    return this.unavailable(req, text);
  }

  // ============================================================== inventory
  private onHand(store: Store, materialId: string): number {
    return store.rows('stock_movement').filter((m) => m.material_id === materialId).reduce((t, m) => t + num(m.quantity), 0);
  }

  inventory(req: LocalRequest, store: Store, me: Me, seg: string[], text: (k: 'offline' | 'notFound') => string): LocalResponse {
    const sub = seg[2];
    const clinic = this.clinic(store);
    const currency = String(clinic.default_currency ?? 'USD');

    if (sub === 'materials' && req.method === 'GET' && !seg[3]) {
      const movements = store.rows('stock_movement');
      const materials = store
        .rows('material')
        .sort((a, b) => Number(b.active !== false) - Number(a.active !== false) || String(a.name_en).localeCompare(String(b.name_en)))
        .map((m) => {
          const mine = movements.filter((x) => x.material_id === m.id);
          const onHand = mine.reduce((t, x) => t + num(x.quantity), 0);
          const receipts = mine.filter((x) => x.kind === 'receipt' && x.unit_cost_minor !== null && x.unit_cost_minor !== undefined).map((x) => ({ quantity: num(x.quantity), unitCostMinor: num(x.unit_cost_minor), currency: String(x.currency ?? currency) }));
          const unitCost = weightedAverageUnitCost(receipts, currency);
          const preferred = m.preferred_supplier_id ? store.getRow('supplier', String(m.preferred_supplier_id)) : null;
          const last = m.last_supplier_id ? store.getRow('supplier', String(m.last_supplier_id)) : null;
          return {
            id: m.id, sku: m.sku, nameEn: m.name_en, nameAr: m.name_ar, category: m.category, baseUnit: m.base_unit, packUnit: m.pack_unit, packSize: num(m.pack_size) || 1, reorderThreshold: num(m.reorder_threshold), location: m.storage_location ?? null, active: m.active !== false,
            onHand, level: stockLevel(onHand, num(m.reorder_threshold)), unitCostMinor: unitCost.minor, valueMinor: inventoryValue(onHand, unitCost).minor,
            preferredSupplier: preferred ? { id: preferred.id, name: preferred.name } : null, lastSupplier: last ? { id: last.id, name: last.name } : null,
          };
        });
      return { status: 200, body: { costingMethod: 'weighted_average', currency, materials, offline: true } };
    }
    if (sub === 'movements' && req.method === 'POST') {
      const body = (req.body ?? {}) as Record<string, unknown>;
      const material = store.getRow('material', String(body.materialId ?? ''));
      if (!material) return this.notFound(text);
      const kind = String(body.kind ?? 'issue');
      const baseUnits = body.packQuantity !== undefined && body.packQuantity !== null ? packsToBaseUnits(num(body.packQuantity), num(material.pack_size) || 1) : Math.abs(num(body.quantity));
      if (!(baseUnits > 0) && kind !== 'adjustment') return { status: 400, body: { code: 'validation_failed', message: 'Provide either quantity or packQuantity.' } };
      const negative = ['issue', 'waste', 'expiry', 'transfer_out'].includes(kind);
      const signed = kind === 'adjustment' ? (body.quantity !== undefined ? num(body.quantity) : baseUnits) : negative ? -baseUnits : baseUnits;
      if (signed === 0) return { status: 400, body: { code: 'validation_failed', message: 'A movement of nothing is not a movement.' } };
      const id = typeof body.id === 'string' ? body.id : randomUUID();
      const occurredAt = typeof body.occurredAt === 'string' ? iso(body.occurredAt) : nowIso();
      const row: Row = { id, clinic_id: this.engine.active?.clinicId, material_id: material.id, batch_id: null, kind, quantity: signed, reason: body.reason ?? null, patient_id: body.patientId ?? null, treatment_id: body.treatmentId ?? null, unit_cost_minor: body.unitCostMinor !== undefined ? num(body.unitCostMinor) : null, currency: body.unitCostMinor !== undefined ? currency : null, source_key: null, occurred_at: occurredAt, recorded_by: me.user.id };
      this.engine.record({ entity: 'stock_movement', entityId: id, action: 'post', payload: { ...body, id: undefined, occurredAt }, rows: [{ entity: 'stock_movement', id, data: row, patientId: (body.patientId as string | null) ?? null }] });
      const onHand = this.onHand(store, String(material.id));
      return { status: 201, body: { id, onHand, level: stockLevel(onHand, num(material.reorder_threshold)), warning: onHand < 0 ? 'Stock is negative — records disagree with reality. Reconcile a count.' : null, provisional: true } };
    }
    if (sub === 'suppliers' && req.method === 'GET' && !seg[3]) {
      const suppliers = store
        .rows('supplier')
        .sort((a, b) => Number(b.active !== false) - Number(a.active !== false) || String(a.name).localeCompare(String(b.name)))
        .map((s) => ({ id: s.id, name: s.name, contactName: s.contact_name ?? null, phone: s.phone ?? null, whatsappPhone: s.whatsapp_phone ?? null, email: s.email ?? null, address: s.address ?? null, notes: s.notes ?? null, active: s.active !== false, billedMinor: 0, paidMinor: 0, outstandingMinor: 0 }));
      return { status: 200, body: { suppliers, offline: true, moneyUnavailable: true } };
    }
    if (sub === 'alerts' && req.method === 'GET') {
      const lowStock = store
        .rows('material')
        .filter((m) => m.active !== false)
        .map((m) => {
          const onHand = this.onHand(store, String(m.id));
          return { id: m.id, sku: m.sku, nameEn: m.name_en, nameAr: m.name_ar, onHand, threshold: num(m.reorder_threshold), unit: m.base_unit, level: stockLevel(onHand, num(m.reorder_threshold)) };
        })
        .filter((m) => m.level !== 'ok');
      return { status: 200, body: { lowStock, expiring: [], offline: true } };
    }
    return this.unavailable(req, text);
  }

  // ============================================================= laboratory
  private orderView(store: Store, o: Row) {
    const lab = store.getRow('laboratory', String(o.laboratory_id));
    const today = localDate(Date.now());
    const status = String(o.status);
    return {
      id: o.id,
      itemType: o.item_type,
      treatmentId: o.treatment_id ?? null,
      teeth: (o.teeth as string[]) ?? [],
      shade: o.shade ?? null,
      status,
      dueOn: o.due_on ?? null,
      overdue: !!o.due_on && String(o.due_on).slice(0, 10) < today && !['completed', 'cancelled', 'received', 'fitting'].includes(status),
      labCostMinor: num(o.lab_cost_minor),
      patientChargeMinor: num(o.patient_charge_minor),
      currency: o.currency,
      laboratory: { id: o.laboratory_id, name: lab?.name ?? '', whatsappPhone: lab?.whatsapp_phone ?? null },
      patient: this.patientRef(store, String(o.patient_id)),
      createdAt: o.created_at,
      allowedNext: LAB_ORDER_TRANSITIONS[status as keyof typeof LAB_ORDER_TRANSITIONS] ?? [],
      allStates: LAB_ORDER_STATES,
      specification: o.specification ?? null,
      instructions: o.instructions ?? null,
      laboratoryId: o.laboratory_id,
      payableRecorded: !!o.payable_source_key,
      provisional: (o as { __provisional?: boolean }).__provisional === true,
    };
  }

  lab(req: LocalRequest, store: Store, me: Me, seg: string[], text: (k: 'offline' | 'notFound') => string): LocalResponse {
    const sub = seg[2];
    const clinic = this.clinic(store);
    const currency = String(clinic.default_currency ?? 'USD');

    if (sub === 'laboratories' && req.method === 'GET' && !seg[3]) {
      const orders = store.rows('lab_order');
      const payments = store.rows('payment').filter((p) => p.direction === 'outgoing' && p.laboratory_id && !p.reverses_id && !this.reversed(store, String(p.id)));
      const laboratories = store
        .rows('laboratory')
        .sort((a, b) => Number(b.active !== false) - Number(a.active !== false) || String(a.name).localeCompare(String(b.name)))
        .map((l) => {
          const mine = orders.filter((o) => o.laboratory_id === l.id);
          const billedMinor = mine.filter((o) => o.payable_source_key).reduce((t, o) => t + num(o.lab_cost_minor), 0);
          const pendingMinor = mine.filter((o) => !o.payable_source_key && !['draft', 'cancelled'].includes(String(o.status))).reduce((t, o) => t + num(o.lab_cost_minor), 0);
          const paidMinor = payments.filter((p) => p.laboratory_id === l.id).reduce((t, p) => t + num(p.amount_minor), 0);
          return { id: l.id, name: l.name, contactName: l.contact_name ?? null, phone: l.phone ?? null, whatsappPhone: l.whatsapp_phone ?? null, email: l.email ?? null, address: l.address ?? null, notes: l.notes ?? null, active: l.active !== false, billedMinor, paidMinor, outstandingMinor: billedMinor - paidMinor, pendingMinor };
        });
      return { status: 200, body: { laboratories, offline: true } };
    }
    if (sub === 'orders') {
      const id = seg[3];
      if (req.method === 'GET' && !id) {
        const status = req.query.get('status');
        const patientId = req.query.get('patientId');
        const list = store
          .rows('lab_order')
          .filter((o) => (!status || o.status === status) && (!patientId || o.patient_id === patientId))
          .sort((a, b) => Date.parse(String(b.created_at)) - Date.parse(String(a.created_at)))
          .slice(0, 200);
        return { status: 200, body: { orders: list.map((o) => this.orderView(store, o)), offline: true } };
      }
      if (req.method === 'POST' && !id) {
        const body = (req.body ?? {}) as Record<string, unknown>;
        const patientId = String(body.patientId ?? '');
        if (!store.getRow('patient', patientId)) return this.notFound(text);
        const lab = store.getRow('laboratory', String(body.laboratoryId ?? ''));
        if (!lab) return { status: 404, body: { code: 'laboratory_not_found', message: 'That laboratory does not exist in this clinic.' } };
        if (lab.active === false) return { status: 409, body: { code: 'laboratory_retired', message: 'That laboratory has been retired. Bring it back first.' } };
        const orderId = typeof body.id === 'string' ? body.id : randomUUID();
        const eventId = randomUUID();
        const row: Row = { id: orderId, clinic_id: this.engine.active?.clinicId, patient_id: patientId, laboratory_id: lab.id, dentist_membership_id: me.user.membershipId, treatment_id: body.treatmentId ?? null, item_type: body.itemType, teeth: (body.teeth as string[]) ?? [], shade: body.shade ?? null, specification: body.specification ?? null, instructions: body.instructions ?? null, status: 'draft', sent_at: null, due_on: body.dueOn ?? null, received_at: null, completed_at: null, lab_cost_minor: num(body.labCostMinor), patient_charge_minor: num(body.patientChargeMinor), currency: typeof body.currency === 'string' ? body.currency : currency, remake_of_id: null, payable_source_key: null, created_at: nowIso(), created_by: me.user.id, updated_at: nowIso(), version: 1 };
        const event: Row = { id: eventId, clinic_id: this.engine.active?.clinicId, lab_order_id: orderId, from_status: null, to_status: 'draft', note: null, actor_id: me.user.id, actor_label: me.user.fullName, occurred_at: nowIso() };
        const dependsOn = typeof body.treatmentId === 'string' ? store.pendingFor(body.treatmentId) : [];
        this.engine.record({ entity: 'lab_order', entityId: orderId, action: 'create', payload: { ...body, id: undefined }, dependsOn, rows: [{ entity: 'lab_order', id: orderId, data: row, patientId }, { entity: 'lab_order_event', id: eventId, data: event, patientId }] });
        return { status: 201, body: { id: orderId, provisional: true } };
      }
      if (!id) return this.notFound(text);
      const order = store.getRow('lab_order', id);
      if (!order) return this.notFound(text);
      const patientId = String(order.patient_id);
      if (req.method === 'GET' && seg[4] === 'events') {
        const events = store.rows('lab_order_event').filter((e) => e.lab_order_id === id).sort((a, b) => Date.parse(String(a.occurred_at)) - Date.parse(String(b.occurred_at)));
        return { status: 200, body: { events: events.map((e) => ({ id: e.id, from: e.from_status ?? null, to: e.to_status, note: e.note ?? null, actor: e.actor_label ?? null, at: e.occurred_at })) } };
      }
      if (req.method === 'POST' && seg[4] === 'status') {
        const body = (req.body ?? {}) as Record<string, unknown>;
        const next = String(body.status ?? '');
        const correction = body.correction === true;
        if (!(LAB_ORDER_STATES as readonly string[]).includes(next)) return { status: 400, body: { code: 'validation_failed', message: 'Unknown order status.' } };
        if (correction && String(body.note ?? '').trim().length < 3) return { status: 400, body: { code: 'note_required', message: "Say why the status is being corrected — it goes in the order's history." } };
        const current = String(order.status);
        const allowed = LAB_ORDER_TRANSITIONS[current as keyof typeof LAB_ORDER_TRANSITIONS] ?? [];
        if (current === next) return { status: 200, body: { id, status: next, allowedNext: LAB_ORDER_TRANSITIONS[next as keyof typeof LAB_ORDER_TRANSITIONS] ?? [] } };
        if (!correction && !(allowed as readonly string[]).includes(next)) return { status: 400, body: { code: 'invalid_transition', message: `A ${current.replace(/_/g, ' ')} order cannot move to ${next.replace(/_/g, ' ')}.`, allowed } };
        const at = nowIso();
        const receivedNow = LAB_RECEIVED_STATES.has(next as never) && !order.payable_source_key;
        store.patchRow('lab_order', id, { status: next, updated_at: at, ...(next === 'awaiting_pickup' ? { sent_at: at } : {}), ...(next === 'received' ? { received_at: at } : {}), ...(next === 'completed' ? { completed_at: at } : {}), ...(receivedNow ? { payable_source_key: `local:${id}:payable` } : {}) });
        const eventId = randomUUID();
        const event: Row = { id: eventId, clinic_id: this.engine.active?.clinicId, lab_order_id: id, from_status: current, to_status: next, note: correction ? `Correction: ${String(body.note ?? '')}` : (body.note ?? null), actor_id: me.user.id, actor_label: me.user.fullName, occurred_at: at };
        this.engine.record({ entity: 'lab_order', entityId: id, action: 'update', payload: { status: next, ...(typeof body.note === 'string' ? { note: body.note } : {}), ...(correction ? { correction: true } : {}) }, rows: [{ entity: 'lab_order_event', id: eventId, data: event, patientId }] });
        return { status: 200, body: { id, status: next, allowedNext: LAB_ORDER_TRANSITIONS[next as keyof typeof LAB_ORDER_TRANSITIONS] ?? [] } };
      }
      if (req.method === 'PATCH' && seg.length === 4) {
        const body = (req.body ?? {}) as Record<string, unknown>;
        const map: Record<string, string> = { laboratoryId: 'laboratory_id', itemType: 'item_type', teeth: 'teeth', shade: 'shade', specification: 'specification', instructions: 'instructions', dueOn: 'due_on', labCostMinor: 'lab_cost_minor', patientChargeMinor: 'patient_charge_minor' };
        const patch: Row = { updated_at: nowIso() };
        for (const [k, column] of Object.entries(map)) if (k in body) patch[column] = body[k];
        if (body.laboratoryId && body.laboratoryId !== order.laboratory_id && order.status !== 'draft') return { status: 409, body: { code: 'already_sent', message: 'The laboratory can only be changed while the order is a draft.' } };
        store.patchRow('lab_order', id, patch);
        this.engine.record({ entity: 'lab_order', entityId: id, action: 'update', payload: body, rows: [] });
        return { status: 200, body: { id, updated: true } };
      }
      if (req.method === 'DELETE' && seg.length === 4) {
        if (order.status !== 'draft') return { status: 409, body: { code: 'order_already_sent', message: 'This order has already gone to the laboratory. Cancel it instead.' } };
        const events = store.rows('lab_order_event').filter((e) => e.lab_order_id === id);
        this.engine.record({ entity: 'lab_order', entityId: id, action: 'delete', payload: {}, rows: [], deletes: [{ entity: 'lab_order', id }, ...events.map((e) => ({ entity: 'lab_order_event', id: String(e.id) }))] });
        return { status: 200, body: { deleted: true } };
      }
    }
    return this.unavailable(req, text);
  }
}
